import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MultiGraph } from 'graphology';
import forceAtlas2 from 'graphology-layout-forceatlas2';
import Sigma from 'sigma';
import {
  getSelaProjectEvidence,
  getSelaProjectGraph,
  resetSelaProjectGraph,
  type SelaGraphEdge,
  type SelaGraphNode,
  type SelaGraphPayload,
  type SelaProjectDetail,
  type SelaSourceRef,
} from '../../services/selaApi';
import { useAskSela } from './ask-sela/AskSelaContext';

type WorkspaceContext = { project: SelaProjectDetail };

type EvidenceState = {
  loading: boolean;
  sources: SelaSourceRef[];
  node?: SelaGraphNode | null;
  error?: string;
};

// ---------------------------------------------------------------------------
// Hub vocabulary — mirrors backend graph_schema.py categorize() buckets.
// ---------------------------------------------------------------------------

type HubKey =
  | 'BuildingProgram'
  | 'Site'
  | 'Parking'
  | 'Stairs'
  | 'Envelope'
  | 'ProjectAdmin'
  | 'Permits'
  | 'ZoningDistrict';

const HUB_META: Record<HubKey, { label: string; bg: string; border: string; text: string }> = {
  BuildingProgram: { label: 'Building',            bg: '#ede9fe', border: '#6d28d9', text: '#3b0764' },
  Site:            { label: 'Site & lot',           bg: '#fef3e2', border: '#c97a16', text: '#7c4a09' },
  Parking:         { label: 'Parking',              bg: '#fae8ff', border: '#a21caf', text: '#581c87' },
  Stairs:          { label: 'Stairs & egress',      bg: '#fee2e2', border: '#b91c1c', text: '#7f1d1d' },
  Envelope:        { label: 'Envelope & materials', bg: '#fef9c3', border: '#a16207', text: '#713f12' },
  ProjectAdmin:    { label: 'Project admin',        bg: '#f1f5f9', border: '#475569', text: '#1e293b' },
  Permits:         { label: 'Permits & violations', bg: '#dbeafe', border: '#1d4ed8', text: '#1e3a8a' },
  ZoningDistrict:  { label: 'Zoning & code',        bg: '#e8f7ee', border: '#1f8a4a', text: '#0c5a30' },
};

const HUB_ORDER: HubKey[] = [
  'BuildingProgram', 'Site', 'Parking', 'Stairs',
  'Envelope', 'Permits', 'ProjectAdmin', 'ZoningDistrict',
];

const HUB_KEYS = new Set<HubKey>(HUB_ORDER);
const isHubType = (t: string): t is HubKey => HUB_KEYS.has(t as HubKey);

const CATEGORY_META: Record<string, { label: string; color: string; bg: string }> = {
  zoning: { label: 'Zoning & code', color: '#0c5a30', bg: '#e8f7ee' },
  site: { label: 'Site & lot', color: '#7c4a09', bg: '#fef3e2' },
  trees: { label: 'Trees & arborist', color: '#14532d', bg: '#dcfce7' },
  dimensions: { label: 'Heights & dimensions', color: '#1e3a8a', bg: '#dbe9ff' },
  building: { label: 'Building program', color: '#3b0764', bg: '#ede9fe' },
  parking: { label: 'Parking', color: '#581c87', bg: '#fae8ff' },
  stairs: { label: 'Stairs & egress', color: '#7f1d1d', bg: '#fee2e2' },
  envelope: { label: 'Envelope & materials', color: '#713f12', bg: '#fef9c3' },
  openings: { label: 'Openings', color: '#164e63', bg: '#cffafe' },
  permits: { label: 'Permits & violations', color: '#1e3a8a', bg: '#dbeafe' },
  admin: { label: 'Project admin', color: '#1e293b', bg: '#f1f5f9' },
  other: { label: 'Other', color: '#1e293b', bg: '#e2e8f0' },
};

function categoryOf(node: SelaGraphNode): string {
  const category = (node.category || '').trim().toLowerCase();
  return category && CATEGORY_META[category] ? category : 'other';
}

function confidenceTone(confidence?: number): string {
  if (typeof confidence !== 'number') return 'bg-slate-100 text-slate-700';
  if (confidence >= 0.8) return 'bg-emerald-50 text-emerald-700';
  if (confidence >= 0.5) return 'bg-amber-50 text-amber-700';
  return 'bg-rose-50 text-rose-700';
}

function sourcesOf(node: SelaGraphNode): SelaSourceRef[] {
  if (node.sources?.length) return node.sources;
  if (node.evidenceRefs?.length) return node.evidenceRefs;
  if (typeof node.pageNumber === 'number') return [{ pageNumber: node.pageNumber }];
  return [];
}

function firstQuote(sources: SelaSourceRef[]): string {
  return sources.find((source) => source.quote)?.quote || '';
}

function pageLabel(source: SelaSourceRef, fallback = 'Source'): string {
  return [
    source.sheetNumber ? `Sheet ${source.sheetNumber}` : '',
    typeof source.pageNumber === 'number' ? `p.${source.pageNumber}` : '',
  ].filter(Boolean).join(' · ') || source.documentName || fallback;
}

function shouldFetchEvidence(node: SelaGraphNode | null): node is SelaGraphNode {
  return node?.nodeType === 'ExtractedFact';
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

type TypeFilter = 'all' | 'project' | 'hubs' | 'fact' | 'missing';

const TYPE_FILTERS: { id: TypeFilter; label: string }[] = [
  { id: 'all',     label: 'All' },
  { id: 'project', label: 'Project' },
  { id: 'hubs',    label: 'Attributes' },
  { id: 'fact',    label: 'Extracted facts' },
  { id: 'missing', label: 'Missing facts' },
];

function matchesTypeFilter(node: SelaGraphNode, filter: TypeFilter): boolean {
  switch (filter) {
    case 'all':     return true;
    case 'project': return ['Project', 'Address', 'Parcel'].includes(node.nodeType);
    case 'hubs':    return isHubType(node.nodeType);
    case 'fact':    return node.nodeType === 'ExtractedFact';
    case 'missing': return node.nodeType === 'MissingFact';
  }
}

// ---------------------------------------------------------------------------
// Sigma node attribute helpers
// ---------------------------------------------------------------------------

function nodeColor(n: SelaGraphNode, hubByFact: Map<string, HubKey>): string {
  if (n.nodeType === 'Project') return '#0f172a';
  if (n.nodeType === 'Address' || n.nodeType === 'Parcel') return '#d97706';
  if (isHubType(n.nodeType)) return HUB_META[n.nodeType].border;
  if (n.nodeType === 'ExtractedFact' || n.nodeType === 'MissingFact') {
    const hub = hubByFact.get(n.nodeId);
    if (hub) {
      return n.nodeType === 'MissingFact' ? HUB_META[hub].bg : HUB_META[hub].border;
    }
  }
  return '#94a3b8';
}

function nodeSize(n: SelaGraphNode): number {
  if (n.nodeType === 'Project') return 14;
  if (isHubType(n.nodeType)) return 11;
  if (n.nodeType === 'Address' || n.nodeType === 'Parcel') return 9;
  // Facts large enough to be visible as a dot-cloud at default zoom.
  return n.nodeType === 'MissingFact' ? 7 : 9;
}

// ---------------------------------------------------------------------------
// Graphology graph builder + ForceAtlas2 layout
// ---------------------------------------------------------------------------

function buildSigmaGraph(
  visibleNodes: SelaGraphNode[],
  allEdges: SelaGraphEdge[],
  hubByFact: Map<string, HubKey>,
): MultiGraph {
  const g = new MultiGraph({ type: 'directed', multi: true });

  // Pre-compute hub ring angles for seeding positions.
  const presentHubs: HubKey[] = HUB_ORDER.filter((h) =>
    visibleNodes.some((n) => n.nodeType === h),
  );
  const hubAngle = new Map<HubKey, number>();
  presentHubs.forEach((h, i) => {
    hubAngle.set(h, (i / Math.max(1, presentHubs.length)) * Math.PI * 2 - Math.PI / 2);
  });

  for (const n of visibleNodes) {
    let x = 0;
    let y = 0;
    if (n.nodeType === 'Project') {
      x = 0; y = 0;
    } else if (n.nodeType === 'Address') {
      x = -30; y = -70;
    } else if (n.nodeType === 'Parcel') {
      x = 30; y = -70;
    } else if (isHubType(n.nodeType)) {
      const a = hubAngle.get(n.nodeType) ?? 0;
      // Compact hub ring — keep everything in a small coordinate space so
      // fact dots are visible at the initial camera zoom.
      x = Math.cos(a) * 120;
      y = Math.sin(a) * 120;
    } else {
      const hub = hubByFact.get(n.nodeId);
      const base = hub ? (hubAngle.get(hub) ?? 0) : Math.random() * Math.PI * 2;
      // Seed facts very close to their hub — FA2 will tighten the clusters.
      const r = 145 + Math.random() * 30;
      const jitter = (Math.random() - 0.5) * 0.7;
      x = Math.cos(base + jitter) * r + (Math.random() - 0.5) * 20;
      y = Math.sin(base + jitter) * r + (Math.random() - 0.5) * 20;
    }

    g.addNode(n.nodeId, {
      x,
      y,
      label: n.label || n.nodeId,
      color: nodeColor(n, hubByFact),
      size: nodeSize(n),
      nodeType: n.nodeType,
      // Store full node data for the side panel lookup.
      _data: n,
    });
  }

  const visibleIds = new Set(visibleNodes.map((n) => n.nodeId));
  for (const e of allEdges) {
    if (!visibleIds.has(e.sourceNodeId) || !visibleIds.has(e.targetNodeId)) continue;
    if (e.sourceNodeId === e.targetNodeId) continue;
    try {
      g.addDirectedEdge(e.sourceNodeId, e.targetNodeId, {
        predicate: e.predicate,
        color: '#cbd5e1',
        size: 0.6,
      });
    } catch (_) {
      // multigraph: duplicate edges allowed, ignore key collision
    }
  }

  // Run ForceAtlas2 synchronously (~200 nodes runs in <80 ms).
  // Strong gravity + low scalingRatio = tight clusters around hubs.
  const settings = forceAtlas2.inferSettings(g);
  forceAtlas2.assign(g, {
    iterations: 400,
    settings: {
      ...settings,
      gravity: 1.5,
      scalingRatio: 1.5,
      strongGravityMode: true,
      barnesHutOptimize: g.order > 80,
      linLogMode: false,
    },
  });

  return g;
}

// Compute screen-space label anchors from the rendered cluster bounds.
// Keeping the label gap in pixels prevents tiny clusters from sending labels
// far away when Sigma rescales a sparse graph.
function computeHubLabelPositions(
  sigma: Sigma,
  graph: MultiGraph,
  hubByFact: Map<string, HubKey>,
): Map<HubKey, { x: number; y: number }> {
  const data = new Map<HubKey, { sx: number; bottom: number; count: number }>();

  const addNode = (nodeId: string, hub: HubKey) => {
    try {
      const vp = sigma.graphToViewport({
        x: graph.getNodeAttribute(nodeId, 'x') as number,
        y: graph.getNodeAttribute(nodeId, 'y') as number,
      });
      if (!data.has(hub)) data.set(hub, { sx: 0, bottom: -Infinity, count: 0 });
      const d = data.get(hub)!;
      d.sx += vp.x;
      if (vp.y > d.bottom) d.bottom = vp.y;
      d.count += 1;
    } catch (_) {}
  };

  graph.forEachNode((nodeId, attrs) => {
    const hub = hubByFact.get(nodeId);
    if (hub) addNode(nodeId, hub);

    const nt = attrs.nodeType as string;
    if (isHubType(nt)) addNode(nodeId, nt);
  });

  const positions = new Map<HubKey, { x: number; y: number }>();
  data.forEach(({ sx, bottom, count }, hub) => {
    if (count === 0) return;
    positions.set(hub, { x: sx / count, y: bottom + 12 });
  });
  return positions;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SelaProjectGraphPage() {
  const { project } = useOutletContext<WorkspaceContext>();
  const projectId = project.id;

  const [payload, setPayload] = useState<SelaGraphPayload>({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelaGraphNode | null>(null);
  const [evidenceByNodeId, setEvidenceByNodeId] = useState<Record<string, EvidenceState>>({});
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [activeHubs, setActiveHubs] = useState<Set<HubKey>>(new Set(HUB_ORDER));
  const [search, setSearch] = useState('');
  const [resetting, setResetting] = useState(false);

  // Sigma rendering refs
  const containerRef = useRef<HTMLDivElement>(null);
  const sigmaRef = useRef<Sigma | null>(null);
  // Refs shared into sigma's reducers without triggering rebuild.
  const focusSetRef = useRef<Set<string> | null>(null);
  const graphRef = useRef<MultiGraph | null>(null);
  const evidenceRequestsRef = useRef<Map<string, Promise<EvidenceState>>>(new Map());

  // Cluster label positions in screen-space (px relative to sigma canvas).
  const [clusterPositions, setClusterPositions] = useState<Map<HubKey, { x: number; y: number }>>(
    new Map(),
  );

  // ---- Data loading ----

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const next = await getSelaProjectGraph(projectId);
      setPayload(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load graph.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { void reload(); }, [reload]);

  useEffect(() => {
    if (!shouldFetchEvidence(selected)) return;
    const nodeId = selected.nodeId;
    const existing = evidenceByNodeId[nodeId];
    // Skip only after a completed fetch. Treating any cached entry (including
    // loading: true) as "done" used to re-run this effect when we set loading
    // state, cancel the in-flight request, and leave the panel stuck.
    if (existing && !existing.loading) return;

    if (!existing?.loading) {
      setEvidenceByNodeId((current) => ({
        ...current,
        [nodeId]: { loading: true, sources: sourcesOf(selected) },
      }));
    }

    let request = evidenceRequestsRef.current.get(nodeId);
    if (!request) {
      request = getSelaProjectEvidence(projectId, nodeId)
        .then((detail) => ({
          loading: false,
          node: detail.node,
          sources: detail.sources.length ? detail.sources : sourcesOf(selected),
        }))
        .catch((err) => ({
          loading: false,
          sources: sourcesOf(selected),
          error: err instanceof Error ? err.message : 'Evidence unavailable.',
        }));
      evidenceRequestsRef.current.set(nodeId, request);
    }

    let cancelled = false;
    void request.then((next) => {
      if (cancelled) return;
      setEvidenceByNodeId((current) => ({ ...current, [nodeId]: next }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evidenceByNodeId is read for cache hits only; including it re-triggers the loading-state cancel bug above.
  }, [projectId, selected]);

  // ---- Derived state (same logic as before) ----

  const hubByFact = useMemo(() => {
    const map = new Map<string, HubKey>();
    for (const e of payload.edges) {
      if (e.predicate !== 'HAS_FACT' && e.predicate !== 'HAS_MISSING_FACT') continue;
      const src = payload.nodes.find((n) => n.nodeId === e.sourceNodeId);
      if (src && isHubType(src.nodeType)) map.set(e.targetNodeId, src.nodeType);
    }
    return map;
  }, [payload]);

  const counts = useMemo(() => {
    const c: Record<TypeFilter, number> = { all: 0, project: 0, hubs: 0, fact: 0, missing: 0 };
    for (const n of payload.nodes) {
      c.all += 1;
      if (matchesTypeFilter(n, 'project')) c.project += 1;
      if (matchesTypeFilter(n, 'hubs')) c.hubs += 1;
      if (matchesTypeFilter(n, 'fact')) c.fact += 1;
      if (matchesTypeFilter(n, 'missing')) c.missing += 1;
    }
    return c;
  }, [payload.nodes]);

  const hubCounts = useMemo(() => {
    const c = Object.fromEntries(HUB_ORDER.map((k) => [k, 0])) as Record<HubKey, number>;
    for (const n of payload.nodes) {
      if (n.nodeType !== 'ExtractedFact' && n.nodeType !== 'MissingFact') continue;
      const hub = hubByFact.get(n.nodeId);
      if (hub) c[hub] += 1;
    }
    return c;
  }, [payload.nodes, hubByFact]);

  const visibleNodes = useMemo(() => {
    const term = search.trim().toLowerCase();
    return payload.nodes.filter((n) => {
      if (!matchesTypeFilter(n, typeFilter)) return false;
      if (n.nodeType === 'ExtractedFact' || n.nodeType === 'MissingFact') {
        const hub = hubByFact.get(n.nodeId);
        if (hub && !activeHubs.has(hub)) return false;
      } else if (isHubType(n.nodeType)) {
        if (!activeHubs.has(n.nodeType)) return false;
      }
      if (term) {
        const hay = `${n.label || ''} ${n.value || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [payload.nodes, typeFilter, activeHubs, search, hubByFact]);

  // ---- Sigma lifecycle: rebuild graph+renderer when visible set changes ----

  useEffect(() => {
    if (!containerRef.current) return;

    // Kill existing renderer before rebuilding.
    if (sigmaRef.current) {
      sigmaRef.current.kill();
      sigmaRef.current = null;
    }
    graphRef.current = null;

    if (visibleNodes.length === 0) {
      setClusterPositions(new Map());
      return;
    }

    const graph = buildSigmaGraph(visibleNodes, payload.edges, hubByFact);
    graphRef.current = graph;

    // Pre-build adjacency so reducers can cheaply check neighborhood.
    const adjacency = new Map<string, Set<string>>();
    graph.forEachEdge((_e, _ea, src, tgt) => {
      if (!adjacency.has(src)) adjacency.set(src, new Set());
      if (!adjacency.has(tgt)) adjacency.set(tgt, new Set());
      adjacency.get(src)!.add(tgt);
      adjacency.get(tgt)!.add(src);
    });

    const getFocusSet = (id: string): Set<string> => {
      const s = new Set<string>([id]);
      (adjacency.get(id) ?? new Set<string>()).forEach((n) => s.add(n));
      return s;
    };

    const sigma = new Sigma(graph, containerRef.current!, {
      renderLabels: true,
      // Hub nodes (size 11) show labels; fact nodes (size 9) need zoom ≥ 1.5x.
      // This mirrors the NeurIPS style: cluster labels always visible, individual
      // fact labels only when zoomed in.
      labelRenderedSizeThreshold: 10,
      labelFont: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      labelSize: 10,
      labelWeight: '500',
      labelColor: { color: '#1e293b' },
      defaultEdgeColor: '#dde3ec',
      nodeReducer: (node, attrs) => {
        const focus = focusSetRef.current;
        if (focus && !focus.has(node)) {
          // Dim non-adjacent nodes; hide their labels.
          return { ...attrs, color: '#e8ecf3', label: undefined, zIndex: 0 };
        }
        return { ...attrs, zIndex: focus ? 1 : 0 };
      },
      edgeReducer: (_edge, attrs) => {
        // Edges are structural, not decorative — make them nearly invisible.
        return { ...attrs, color: '#dde3ec', size: 0.5 };
      },
    });

    sigmaRef.current = sigma;

    // Click a node → select it and highlight its neighbourhood.
    sigma.on('clickNode', ({ node }) => {
      const data = graph.getNodeAttribute(node, '_data') as SelaGraphNode | undefined;
      setSelected(data ?? null);
      focusSetRef.current = getFocusSet(node);
      sigma.refresh();
    });

    // Click empty canvas → clear selection.
    sigma.on('clickStage', () => {
      setSelected(null);
      focusSetRef.current = null;
      sigma.refresh();
    });

    const syncLabelPositions = () => {
      setClusterPositions(computeHubLabelPositions(sigma, graph, hubByFact));
    };

    syncLabelPositions();
    sigma.getCamera().on('updated', syncLabelPositions);
    // Re-sync on resize so labels don't drift.
    sigma.on('resize', syncLabelPositions);

    return () => {
      try { sigma.getCamera().removeAllListeners('updated'); } catch (_) {}
      sigma.kill();
      sigmaRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleNodes, payload.edges, hubByFact]);

  // When selection is cleared externally (side-panel Close button), un-highlight.
  useEffect(() => {
    if (!selected) {
      focusSetRef.current = null;
      sigmaRef.current?.refresh();
    }
  }, [selected]);

  // ---- Interaction ----

  const toggleHub = (id: HubKey) => {
    setActiveHubs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleReset = useCallback(async () => {
    if (!window.confirm('Wipe every node and edge for this project? The next PDF upload will repopulate it.')) return;
    setResetting(true);
    try {
      await resetSelaProjectGraph(projectId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset graph.');
    } finally {
      setResetting(false);
    }
  }, [projectId, reload]);

  // ---- Render ----

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      {/* ── Toolbar ────────────────────────────────────────────────── */}
      <div className="border-b border-[#e7e9ee] bg-white">
        <div className="flex items-center justify-between px-6 pt-3">
          <div>
            <h1 className="text-lg font-semibold text-[#0f172a]">Knowledge graph</h1>
            <p className="text-xs text-[#647488]">
              {visibleNodes.length} of {payload.nodes.length} nodes shown · click any node to focus
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search labels..."
              className="w-56 rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs text-[#0f172a] placeholder:text-[#9aa6b8] focus:border-[#3251b7] focus:outline-none"
            />
            <button
              type="button"
              onClick={() => void reload()}
              className="rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-medium text-[#1e4474] hover:border-[#3251b7] hover:text-[#3251b7]"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => void handleReset()}
              disabled={resetting}
              className="rounded-md border border-[#fecaca] bg-white px-3 py-1.5 text-xs font-medium text-[#b91c1c] hover:border-[#b91c1c] disabled:opacity-50"
            >
              {resetting ? 'Resetting...' : 'Reset graph'}
            </button>
          </div>
        </div>

        {/* Type filter pills */}
        <div className="flex flex-wrap items-center gap-1.5 px-6 pb-2 pt-3">
          {TYPE_FILTERS.map((f) => {
            const active = typeFilter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setTypeFilter(f.id)}
                className={`rounded-full px-3 py-1 text-[11px] font-medium transition ${
                  active
                    ? 'bg-[#0f172a] text-white'
                    : 'border border-[#cdd5e1] bg-white text-[#475669] hover:border-[#3251b7] hover:text-[#3251b7]'
                }`}
              >
                {f.label}
                <span className={`ml-1.5 ${active ? 'text-white/70' : 'text-[#9aa6b8]'}`}>
                  {counts[f.id]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Hub toggle pills */}
        <div className="flex flex-wrap items-center gap-1.5 border-t border-[#eef1f5] px-6 py-2">
          <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-[#9aa6b8]">
            Attribute hubs
          </span>
          {HUB_ORDER.map((id) => {
            const meta = HUB_META[id];
            const active = activeHubs.has(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleHub(id)}
                className="flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition"
                style={{
                  borderColor: active ? meta.border : '#e1e8f0',
                  background: active ? meta.bg : '#ffffff',
                  color: active ? meta.text : '#9aa6b8',
                }}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ background: meta.border, opacity: active ? 1 : 0.3 }}
                />
                {meta.label}
                <span className={active ? 'opacity-70' : 'opacity-50'}>{hubCounts[id]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {error ? (
        <div className="border-b border-red-200 bg-red-50 px-6 py-2 text-sm text-red-800">{error}</div>
      ) : null}

      {/* ── Graph canvas ───────────────────────────────────────────── */}
      <div className="relative flex-1 overflow-hidden bg-[#f7fafd]">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-[#647488]">
            Loading graph...
          </div>
        ) : payload.nodes.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-[#647488]">
            <span>No graph data yet.</span>
            <span className="text-xs">Upload a PDF on the Documents tab to populate this view.</span>
          </div>
        ) : (
          <>
            {/* Sigma canvas — fills the container */}
            <div
              ref={containerRef}
              style={{ width: '100%', height: '100%' }}
            />

            {/* Cluster label overlay — large labels above each hub cluster */}
            {clusterPositions.size > 0 && (
              <div className="pointer-events-none absolute inset-0 overflow-hidden">
                {HUB_ORDER.map((hub) => {
                  const pos = clusterPositions.get(hub);
                  if (!pos || !activeHubs.has(hub)) return null;
                  const count = hubCounts[hub];
                  if (count === 0 && typeFilter !== 'hubs') return null;
                  const meta = HUB_META[hub];
                  return (
                    <div
                      key={hub}
                      style={{
                        position: 'absolute',
                        left: pos.x,
                        top: pos.y,
                        transform: 'translate(-50%, 0)',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          color: meta.text,
                          lineHeight: 1.2,
                          // Subtle halo so label reads over colored nodes.
                          textShadow: `0 0 8px ${meta.bg}, 0 0 14px ${meta.bg}, 0 0 20px ${meta.bg}`,
                        }}
                      >
                        {meta.label}
                      </div>
                      {count > 0 && (
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 500,
                            color: meta.text,
                            opacity: 0.65,
                            marginTop: 1,
                            textShadow: `0 0 6px ${meta.bg}`,
                          }}
                        >
                          {count} facts
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Zoom controls */}
            <div className="absolute bottom-4 left-4 flex flex-col gap-1">
              {[
                { label: '+', title: 'Zoom in',  delta: 0.3 },
                { label: '−', title: 'Zoom out', delta: -0.3 },
              ].map(({ label, title, delta }) => (
                <button
                  key={label}
                  type="button"
                  title={title}
                  onClick={() => {
                    const camera = sigmaRef.current?.getCamera();
                    if (!camera) return;
                    camera.animate({ ratio: camera.ratio * Math.exp(-delta) }, { duration: 200 });
                  }}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-[#cdd5e1] bg-white text-sm font-medium text-[#475569] shadow-sm hover:border-[#3251b7] hover:text-[#3251b7]"
                >
                  {label}
                </button>
              ))}
              <button
                type="button"
                title="Fit view"
                onClick={() => sigmaRef.current?.getCamera().animate(
                  { x: 0.5, y: 0.5, ratio: 1 }, { duration: 300 }
                )}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-[#cdd5e1] bg-white text-[10px] font-semibold text-[#475569] shadow-sm hover:border-[#3251b7] hover:text-[#3251b7]"
              >
                ⊡
              </button>
            </div>

            {/* Legend */}
            <div className="pointer-events-none absolute bottom-4 right-4 rounded-lg border border-[#e1e8f0] bg-white/90 p-3 text-[10px] text-[#647488] shadow-sm backdrop-blur-sm">
              <p className="font-semibold uppercase tracking-wide text-[#0f172a]">Legend</p>
              <div className="mt-1.5 flex flex-col gap-1">
                {[
                  { color: '#0f172a', label: 'Project', big: true },
                  { color: '#6d28d9', label: 'Hub node', big: false },
                  { color: '#6d28d9', label: 'Extracted fact', big: false, muted: true },
                  { color: '#b91c1c', label: 'Missing fact', big: false, muted: true },
                ].map(({ color, label, big, muted }) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <span
                      className="inline-block rounded-full"
                      style={{
                        width: big ? 10 : 6,
                        height: big ? 10 : 6,
                        background: muted ? '#e2e8f0' : color,
                        border: muted ? `1.5px solid ${color}` : 'none',
                        flexShrink: 0,
                      }}
                    />
                    {label}
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[9px] opacity-60">Scroll to zoom · drag to pan</p>
            </div>
          </>
        )}

        {selected ? (
          <SelectedNodePanel
            node={selected}
            evidence={evidenceByNodeId[selected.nodeId]}
            onClose={() => setSelected(null)}
          />
        ) : null}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Side panel — unchanged from previous implementation
// ---------------------------------------------------------------------------

function SelectedNodePanel({
  node,
  evidence,
  onClose,
}: {
  node: SelaGraphNode;
  evidence?: EvidenceState;
  onClose: () => void;
}): React.ReactElement {
  const { openAskSela } = useAskSela();
  const detailNode = evidence?.node || node;
  const meta = CATEGORY_META[categoryOf(detailNode)] ?? CATEGORY_META.other;
  const sources = evidence?.sources?.length ? evidence.sources : sourcesOf(detailNode);
  const quote = firstQuote(sources);
  const showEvidence = detailNode.nodeType === 'ExtractedFact';

  return (
    <aside className="absolute right-4 top-4 max-h-[85%] w-[420px] overflow-auto rounded-xl border border-[#e1e8f0] bg-white p-4 shadow-md">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          <span className="rounded-full bg-[#eff2fe] px-2 py-0.5 text-[10px] font-semibold text-[#3251b7]">
            {detailNode.nodeType}
          </span>
          {detailNode.nodeType === 'ExtractedFact' ? (
            <>
              <span
                className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{ background: meta.bg, color: meta.color }}
              >
                {meta.label}
              </span>
              <span className={['rounded-full px-2 py-0.5 text-[10px] font-semibold', confidenceTone(detailNode.confidence)].join(' ')}>
                {typeof detailNode.confidence === 'number' ? `${(detailNode.confidence * 100).toFixed(0)}% confidence` : 'confidence unknown'}
              </span>
            </>
          ) : null}
        </div>
        <button
          type="button"
          className="text-xs text-[#647488] hover:text-[#1e4474]"
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <h3 className="text-sm font-semibold text-[#0f172a]">{detailNode.label}</h3>
      {detailNode.value ? (
        <p className="mt-1 text-sm text-[#475669]">
          {detailNode.value}
          {detailNode.unit ? ` ${detailNode.unit}` : ''}
        </p>
      ) : null}
      <button
        type="button"
        onClick={() => openAskSela({
          type: 'graphNode',
          label: detailNode.label || detailNode.value || detailNode.nodeId,
          sourceId: detailNode.nodeId,
          sourcePage: 'graph',
          summary: String(detailNode.properties?.description || detailNode.value || firstQuote(sources) || ''),
          metadata: {
            nodeType: detailNode.nodeType,
            category: detailNode.category,
            confidence: detailNode.confidence,
            sources: sources.slice(0, 5),
          },
        }, `Explain "${detailNode.label || detailNode.value || detailNode.nodeId}" and what it affects.`)}
        className="mt-3 rounded-md bg-[#3251b7] px-3 py-2 text-xs font-semibold text-white hover:bg-[#28449f]"
      >
        Ask Sela about this fact
      </button>
      {evidence?.loading ? (
        <div className="mt-3 rounded-lg border border-[#e1e8f0] bg-[#f7fafd] px-3 py-2 text-xs text-[#647488]">
          Loading evidence...
        </div>
      ) : null}
      {evidence?.error ? (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {evidence.error}
        </div>
      ) : null}
      {showEvidence ? (
        <div className="mt-3 border-t border-[#e7e9ee] pt-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#647488]">
            {sources.length === 1 ? '1 source' : `${sources.length} sources`}
          </p>
          <blockquote className="mb-3 rounded-lg border-l-4 border-[#3251b7] bg-[#f7fafd] px-4 py-3 text-sm italic text-[#475669]">
            {quote ? `"${quote}"` : 'No quote captured for this fact.'}
          </blockquote>
          {sources.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#cdd5e1] px-4 py-6 text-sm text-[#94a3b8]">
              No evidence source captured for this fact.
            </div>
          ) : (
            <div className="grid gap-3">
              {sources.map((source, index) => (
                <EvidencePreview
                  key={`${source.documentId || 'doc'}-${source.pageNumber || index}-${index}`}
                  source={source}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </aside>
  );
}

function EvidencePreview({ source }: { source: SelaSourceRef }) {
  return (
    <div className="rounded-lg border border-[#e1e8f0] bg-white p-3 shadow-sm">
      {source.pageImageUrl ? (
        <a
          href={source.pageImageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block overflow-hidden rounded-md border border-[#e7e9ee] bg-[#f7fafd] hover:border-[#3251b7]"
        >
          <img
            src={source.pageImageUrl}
            alt={pageLabel(source, 'Evidence page')}
            className="h-36 w-full object-cover"
            loading="lazy"
          />
        </a>
      ) : (
        <div className="flex h-36 w-full items-center justify-center rounded-md border border-dashed border-[#cdd5e1] text-xs text-[#94a3b8]">
          No preview
        </div>
      )}
      <div className="mt-2 text-xs font-semibold text-[#0f172a]">{pageLabel(source)}</div>
      {source.sheetName ? <div className="mt-0.5 text-[11px] text-[#647488]">{source.sheetName}</div> : null}
      {source.documentName ? <div className="mt-0.5 text-[11px] text-[#94a3b8]">{source.documentName}</div> : null}
      {source.quote ? <div className="mt-2 text-xs italic text-[#475669]">"{source.quote}"</div> : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {source.pageImageUrl ? (
          <a
            href={source.pageImageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7]"
          >
            Open page
          </a>
        ) : null}
        {source.mapUrl ? (
          <a
            href={source.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:border-amber-400 hover:bg-amber-100"
          >
            View ECA layer map
          </a>
        ) : null}
        {source.sourceUrl ? (
          <a
            href={source.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex rounded-md border border-[#cdd5e1] bg-white px-3 py-1.5 text-xs font-semibold text-[#1e4474] hover:border-[#3251b7]"
          >
            Open source
          </a>
        ) : null}
      </div>
    </div>
  );
}
